import React, { useState, useEffect } from 'react';
import { lightenColor, ReMixChart } from '../../components/recharts/ReMixCharts';
import { ChartType } from '../../models/chart_model';
import { COLOR_CODES, formatDMY } from '../../common';
import { getCoalStockData } from '../../Rest_api/RestApi';
import { FilterTab } from '../../components/Filters';
import { ChartTable } from '../../components/table/SummaryTable';

interface CoalStockPriceChartProps {
  startDate: string;
  endDate: string;
  isFullscreen?: boolean;
}

interface CoalData {
  date: string;
  current_stock_value: number;
  previous_stock_value: number;
  current_price_value: number;
  previous_price_value: number;
}

interface ApiCoalData {
  date: string;
  actual_stock_in_tt_total: number;
  price_rs_per_ton_indian_coal: number | null;
  actual_stock_in_tt_total_prev_year: number;
  price_rs_per_ton_indian_coal_prev_year: number;
}
const views = ['Chart', 'Table'];

const CoalStockPriceChart: React.FC<CoalStockPriceChartProps> = ({ startDate, endDate, isFullscreen = false }) => {
  const [chartData, setChartData] = useState<CoalData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState(views[0]);

  // Transform API data to chart format
  const transformApiData = (apiData: ApiCoalData[]): CoalData[] => {
    return apiData.map(item => ({
      date: formatDMY(item.date),
      current_stock_value: Math.round(item.actual_stock_in_tt_total * 100) / 100,
      previous_stock_value: Math.round(item.actual_stock_in_tt_total_prev_year * 100) / 100,
      current_price_value: item.price_rs_per_ton_indian_coal ? Math.round(item.price_rs_per_ton_indian_coal * 100) / 100 : 0,
      previous_price_value: Math.round(item.price_rs_per_ton_indian_coal_prev_year * 100) / 100,
    }));
  };

  useEffect(() => {
    const fetchCoalData = async () => {
      setLoading(true);
      
      try {
        const response = await getCoalStockData(startDate, endDate);
        
        if (response.success && response.data.length > 0) {
          const transformedData = transformApiData(response.data);
          setChartData(transformedData);
        } else {
          // Fallback to empty data if API fails
          setChartData([]);
        }
      } catch (error) {
        console.error('Error fetching coal data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCoalData();
  }, [startDate, endDate]);

  const getLegends = () => {
    return [
      {
        name: 'Coal Stock',
        dataKey: 'current_stock_value',
        stroke: COLOR_CODES.secondary,
        unit: 'TT',
        type: ChartType.Bar
      },
      {
        name: 'Coal Stock (Prev. Year)',
        dataKey: 'previous_stock_value',
        stroke: lightenColor(COLOR_CODES.secondary, 30), // Lightened shade of secondary color
        legendColor:lightenColor(COLOR_CODES.secondary, 15),
        unit: 'TT',
        type: ChartType.Bar
      },
      {
        name: 'Coal Price',
        dataKey: 'current_price_value',
        stroke: COLOR_CODES.primary,
        unit: 'Rs/tonne',
        yAxisId: 'right',
        type: ChartType.Line
      },
      {
        name: 'Coal Price (Prev. Year)',
        dataKey: 'previous_price_value',
        stroke: lightenColor(COLOR_CODES.primary, 20), // Lightened shade of primary color
        legendColor: "#f7b288",
        unit: 'Rs/tonne',
        yAxisId: 'right',
        type: ChartType.Line
      }
    ];
  };

    return (
    <div className="text-left h-full w-full flex flex-col">
      <div className={`flex flex-row gap-x-2 ${isFullscreen ? 'justify-end' : 'justify-between'}`}>
        {!isFullscreen && (
          <h3 className="text-xl font-semibold text-gray-800 flex-shrink-0 chartHeading lg:w-[200px]">
            Coal Stock & Price
          </h3>
        )}
        <div className='flex flex-row gap-x-2 pb-2'>
          <FilterTab
            isSmall={!isFullscreen}
            options={views}
            selected={[selectedView]}
            onClick={(view) => setSelectedView(view)}
          />
          {!isFullscreen && (
            <button
              onClick={() => {
                const event = new CustomEvent('openFullscreen', { detail: 'card4' });
                window.dispatchEvent(event);
              }}
              className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100 ml-2"
              title="Open in fullscreen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      <div className="flex-1 min-h-0 relative">
        {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center z-10">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-700 text-sm font-medium">Loading coal data...</p>
            </div>
          </div>
        )}
        
        {chartData.length > 0 ? (
                    <div className='w-full h-full flex flex-col'>
{selectedView === 'Chart' && (
          <ReMixChart
          // chartContainerHeight='80%!important'
            data={chartData as any}
            legends={getLegends()}
            xDataKey="date"
            yAxisLabel="Coal Stock (TT)"
            barCategoryGap={4}
            showBrush={true}
            brushIndex={{
                startIndex: Math.max(0, chartData.length - 30),
                endIndex: chartData.length
              }}
            isSmallLegend={true}
  
            secondYAxisLabel="Coal Price (Rs/tonne)"
            showSecondYAxis={true}
            isTimeSlot={false}
            onlyTitle={false}
          />
          )}
          {selectedView === 'Table' && (
            <ChartTable
              data={chartData as any}
              legends={getLegends()}
              extraKeys={['date']}
              extraLabels={['Date']}
              isSmall={true}
            />
          )}
          </div>
        ) : !loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-gray-500 text-lg mb-2">No coal data available</p>
              <p className="text-gray-400 text-sm">Please check the date range or try again later</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoalStockPriceChart;
